import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Box, Button, Group, Paper, SimpleGrid, Stack, Text} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useState} from 'react';

import type {PredictionBatchSource} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {withoutUnresolvedAaSequenceNodes} from 'graphql-client/WithoutUnresolvedAaSequenceNode';
import {PROTEIN_AMINO_ACIDS_QUERY_WRAPPER} from 'model-hub/ModelHubQueries';
import {
  aaSequenceId,
  generatePredictionBatchName,
  submitBulkPrediction,
} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import ComponentSearchModal from 'model-hub/scientific-models/input-forms/ComponentSearchModal';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import notifications from 'notifications';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import legacyRunModal from 'ui-library/legacyRunModal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';

interface SelectedAaSequence {
  id: string;
  folderId: string;
}

type AntibodyChain = 'heavy' | 'light';

interface AntibodyChainSelectorProps {
  chain: AntibodyChain;
  error: string | null;
  isSelecting: boolean;
  onSelect: (chain: AntibodyChain) => Promise<void>;
  selectedSequence: SelectedAaSequence | null;
}

const CHAIN_LABELS: Record<AntibodyChain, string> = {
  heavy: 'Heavy chain',
  light: 'Light chain',
};

async function hydrateAaSequence(id: string): Promise<SelectedAaSequence | null> {
  const result = await doQuerySafe(PROTEIN_AMINO_ACIDS_QUERY_WRAPPER, {
    variables: {ids: [id]},
  });
  const protein =
    result == null ? null : accessibleObjs(withoutUnresolvedAaSequenceNodes(result.proteins))[0];
  const folderId = protein?.folder?.id;
  const proteinId = protein?.id;
  return folderId == null || proteinId == null ? null : {id: proteinId, folderId};
}

function AntibodyChainSelector({
  chain,
  error,
  isSelecting,
  onSelect,
  selectedSequence,
}: AntibodyChainSelectorProps): JSX.Element {
  const labelId = useId();
  const chainLabel = CHAIN_LABELS[chain];
  const handleSelect = async (): Promise<void> => {
    await onSelect(chain);
  };

  return (
    <Paper component="section" aria-labelledby={labelId} withBorder={true} p="md" radius="md">
      <Stack spacing="md">
        <Group spacing="xs">
          <Text id={labelId} weight={600}>
            {chainLabel}
          </Text>
          <Text size="xs" color="dimmed">
            Required
          </Text>
        </Group>
        {selectedSequence == null ? (
          <Button
            variant="secondary"
            fullWidth={true}
            loading={isSelecting}
            onClick={handleSelect}
            aria-label={`Select ${chainLabel.toLowerCase()} AA sequence`}
          >
            Select AA sequence
          </Button>
        ) : (
          <Group spacing="sm">
            <BenchChip id={selectedSequence.id} size={ChipSize.SMALL} isLink={false} />
            <Button
              variant="ghost"
              loading={isSelecting}
              onClick={handleSelect}
              aria-label={`Change ${chainLabel.toLowerCase()} AA sequence`}
            >
              Change
            </Button>
          </Group>
        )}
        {error != null ? (
          <Alert color="red" role="alert">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}

export interface AntibodyInputFormProps {
  model: ScientificModelNode;
  onRequestClose: () => void;
  onSubmit?: () => Promise<void>;
  source?: PredictionBatchSource;
}

export function AntibodyInputForm({
  model,
  onRequestClose,
  onSubmit,
  source,
}: AntibodyInputFormProps): JSX.Element {
  const [heavyChain, setHeavyChain] = useState<SelectedAaSequence | null>(null);
  const [lightChain, setLightChain] = useState<SelectedAaSequence | null>(null);
  const [chainErrors, setChainErrors] = useState<Record<AntibodyChain, string | null>>({
    heavy: null,
    light: null,
  });
  const [selectingChain, setSelectingChain] = useState<AntibodyChain | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelectChain = async (chain: AntibodyChain): Promise<void> => {
    setSelectingChain(chain);
    setChainErrors((currentErrors) => ({...currentErrors, [chain]: null}));
    try {
      const selectedIds = await legacyRunModal(ComponentSearchModal, {
        availableTypes: [ModelTypeConstants.PROTEIN],
        rowSelectionMode: RowSelectionMode.SINGLE,
        submitButtonText: `Select ${CHAIN_LABELS[chain].toLowerCase()}`,
        title: `Select ${CHAIN_LABELS[chain].toLowerCase()} AA sequence`,
      });
      const selectedId = selectedIds?.[0];
      if (selectedId == null) {
        return;
      }
      if (!APIUtil.isProtein(selectedId)) {
        setChainErrors((currentErrors) => ({
          ...currentErrors,
          [chain]: 'Select an AA sequence.',
        }));
        return;
      }

      const sequence = await hydrateAaSequence(selectedId);
      if (sequence == null) {
        setChainErrors((currentErrors) => ({
          ...currentErrors,
          [chain]: `Unable to load the ${CHAIN_LABELS[chain].toLowerCase()} AA sequence and its folder.`,
        }));
        return;
      }

      if (chain === 'heavy') {
        setHeavyChain(sequence);
      } else {
        setLightChain(sequence);
      }
    } finally {
      setSelectingChain(null);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (heavyChain == null || lightChain == null) {
      return;
    }
    setIsSubmitting(true);
    try {
      const name = await generatePredictionBatchName(model);
      await submitBulkPrediction({
        folderId: heavyChain.folderId,
        model,
        name,
        predictionConfigs: [
          {
            antibody: {
              heavy_chain: aaSequenceId(heavyChain.id),
              light_chain: aaSequenceId(lightChain.id),
            },
          },
        ],
        source,
      });
      if (onSubmit != null) {
        await onSubmit();
      } else {
        onRequestClose();
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch -- error is surfaced via notification; keep the form open for retry
    } catch (error) {
      notifications.notifyError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = heavyChain != null && lightChain != null && selectingChain == null;

  return (
    <Box p="xl" {...getTestProps({component: 'AntibodyInputForm', element: 'root'})}>
      <Stack spacing="xl">
        <Stack spacing="xs">
          <Text size="lg" weight={600}>
            Antibody chains
          </Text>
          <Text size="sm" color="dimmed">
            Choose an existing AA sequence for the heavy and light chains.
          </Text>
        </Stack>
        <SimpleGrid cols={2} spacing="md" breakpoints={[{maxWidth: 'sm', cols: 1}]}>
          <AntibodyChainSelector
            chain="heavy"
            error={chainErrors.heavy}
            isSelecting={selectingChain === 'heavy'}
            onSelect={handleSelectChain}
            selectedSequence={heavyChain}
          />
          <AntibodyChainSelector
            chain="light"
            error={chainErrors.light}
            isSelecting={selectingChain === 'light'}
            onSelect={handleSelectChain}
            selectedSequence={lightChain}
          />
        </SimpleGrid>
        <Group position="right">
          <Button disabled={!canSubmit || isSubmitting} loading={isSubmitting} onClick={handleSubmit}>
            Run prediction
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
